<script setup lang="ts">
/**
 * Star rating — inline SVG so selected stars render truly filled.
 *
 * The icon set in use (lucide) is outline-only and @nuxt/ui renders icons as
 * CSS masks, so `fill-*` utilities have no effect on `i-lucide-star`. Drawing
 * the star ourselves lets us toggle a real `fill` between selected/empty.
 *
 * Display mode (default): rounds `modelValue` and renders read-only.
 * Interactive mode: emits `update:modelValue` (1..max) on click — use v-model.
 */
interface Props {
  modelValue: number
  max?: number
  interactive?: boolean
  sizeClass?: string
}
const props = withDefaults(defineProps<Props>(), {
  max: 5,
  interactive: false,
  sizeClass: 'w-5 h-5',
})
const emit = defineEmits<{ 'update:modelValue': [value: number] }>()

const filledCount = computed(() => Math.round(props.modelValue ?? 0))

function pick(i: number) {
  if (props.interactive) emit('update:modelValue', i)
}

// Classic 5-point star outline (filled when active, outlined when empty).
const STAR_PATH =
  'M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z'
</script>

<template>
  <div class="inline-flex items-center gap-0.5">
    <component
      :is="interactive ? 'button' : 'span'"
      v-for="i in max"
      :key="i"
      :type="interactive ? 'button' : undefined"
      :aria-label="interactive ? String(i) : undefined"
      class="inline-flex"
      :class="interactive ? 'cursor-pointer transition-transform hover:scale-110 focus:outline-none' : ''"
      @click="pick(i)"
    >
      <svg
        viewBox="0 0 24 24"
        :fill="i <= filledCount ? 'currentColor' : 'none'"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linejoin="round"
        aria-hidden="true"
        :class="[sizeClass, i <= filledCount ? 'text-yellow-400' : 'text-gray-300 dark:text-gray-600']"
      >
        <path :d="STAR_PATH" />
      </svg>
    </component>
  </div>
</template>
